import z from "zod";
import { AuditMetaSchema } from "./auditMetaTypes";
import { BoundaryIdSchema, UserIdSchema } from "./idTypes";
import { TenancySchema } from "./tenancyTypes";
import { type RoleName, RoleNameSchema } from "./userContextTypes";

const RoleValues = ["role.collaborator", "role.reader", "role.owner"] as const;
export const RoleSchema = z.enum(RoleValues);

export const GrantOpSchema = z.enum(["op.create", "op.delete", "op.read", "op.update"]);
export type GrantOp = z.infer<typeof GrantOpSchema>;

export const GrantsSchema = z.record(GrantOpSchema, z.boolean()).optional();

// Subset of free-form `/context` resource keys that the UI actively gates on.
const GrantsIds = [
  "cp.assets",
  "cp.chats",
  "cp.concepts",
  "cp.grants",
  "cp.invitations",
  "cp.invitations_domains",
  "cp.invitations_users",
  "cp.label_associations",
  "cp.labels",
  "cp.memories",
  "cp.messages",
  "cp.plans",
  "cp.projects",
  "cp.public_assets",
  "cp.tool_artifacts",
  "cp.user_context",
  "cp.views",
] as const;

export const GrantIdSchema = z.enum(GrantsIds);
export type GrantId = z.infer<typeof GrantIdSchema>;

export type { RoleName };

const DomainRequestSchema = z.object({
  name: z.string(),
});

const InvitedUserRequestSchema = z.object({
  email: z.string(),
});

export const CreateInvitationRequestSchema = z.object({
  domains: z.array(DomainRequestSchema).optional(),
  role: RoleNameSchema,
  users: z.array(InvitedUserRequestSchema).optional(),
});

export const UpdateInvitationRequestSchema = z.object({
  domains: z.array(DomainRequestSchema).optional(),
  role: RoleNameSchema,
  users: z.array(InvitedUserRequestSchema).optional(),
});

const DomainSchema = z.object({
  id: BoundaryIdSchema.optional(),
  invitationId: BoundaryIdSchema.optional(),
  name: z.string(),
});

const IdentifierSchema = z.object({
  id: z.string(), // This is the email
  resource: z.string(),
});

const InvitedUserSchema = z.object({
  id: BoundaryIdSchema.optional(),
  invitationId: BoundaryIdSchema.optional(),
  identifier: IdentifierSchema,
});

const InvitationLinkSchema = z.object({
  token: z.string().optional(),
  url: z.string().optional(),
  destination: z.string().optional(),
});

const ResourceSchema = z.string().optional();

export const InvitationSchema = z
  .object({
    id: BoundaryIdSchema,
    role: RoleNameSchema,
    resource: ResourceSchema,
    resourceId: z.string().optional(),
    domains: z.array(DomainSchema).optional().nullable(),
    humans: z.array(InvitedUserSchema).optional().nullable(),
    link: InvitationLinkSchema,
    grants: GrantsSchema,
  })
  .extend(TenancySchema.shape)
  .extend(AuditMetaSchema.shape);
export type Invitation = z.infer<typeof InvitationSchema>;

export const CreateInvitationsResponseSchema = z.array(InvitationSchema);

export const InvitationsSyncSchema = z.object({
  invites: z.record(BoundaryIdSchema, InvitationSchema),
});

const JoinMethodSchema = z.enum(["joined.email", "joined.domain", "joined.link"]);

export const ProjectParticipantSchema = z.object({
  email: z.string(),
  name: z.string(),
  userId: UserIdSchema,
  addedAt: z.string(),
  role: RoleNameSchema,
  joinMethod: JoinMethodSchema,
});
export type ProjectParticipant = z.infer<typeof ProjectParticipantSchema>;

const ProjectParticipantStatsSchema = z.object({
  firstJoined: z.string().optional(),
  lastJoined: z.string().optional(),
  total: z.number(),
  readers: z.number(),
  collaborators: z.number(),
  owners: z.number(),
  joinedViaEmail: z.number(),
  joinedViaDomain: z.number(),
  joinedViaLink: z.number(),
});

export const ProjectParticipantsResponseSchema = z.object({
  participants: z.array(ProjectParticipantSchema),
  stats: ProjectParticipantStatsSchema,
});
